import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown, Database, Sparkles } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { JurisdictionBadge } from "./JurisdictionBadge";
import { ALL_ONBOARDING_DATA_POINTS } from "@/data/onboardingData";
import { ALL_HOMEBUILDER_DATA_POINTS } from "@/data/homebuilderData";

const ALL_POINTS = [...ALL_ONBOARDING_DATA_POINTS, ...ALL_HOMEBUILDER_DATA_POINTS];

interface SelectionSummaryBarProps {
  count: number;
  labels: string[];
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  showBack?: boolean;
  selectedKeys: Set<string>;
}

export function SelectionSummaryBar({ count, onBack, onNext, nextLabel = "Next", showBack = true, selectedKeys }: SelectionSummaryBarProps) {
  const [expanded, setExpanded] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [delta, setDelta] = useState<number | null>(null);
  const prevCount = useRef(count);

  useEffect(() => {
    if (count !== prevCount.current) {
      const diff = count - prevCount.current;
      setDelta(diff);
      setPulse(true);
      const t1 = setTimeout(() => setPulse(false), 700);
      const t2 = setTimeout(() => setDelta(null), 1600);
      prevCount.current = count;
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [count]);

  const selectedDataPoints = ALL_POINTS.filter(dp => selectedKeys.has(dp.key));

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-primary/20 bg-background shadow-[0_-8px_24px_-12px_hsl(var(--primary)/0.25)]">
      {expanded && (
        <ScrollArea className="h-48 border-b">
          <div className="px-6 py-3 space-y-1">
            {selectedDataPoints.map(dp => (
              <div key={dp.key} className="flex items-center gap-2 text-sm py-0.5">
                <JurisdictionBadge level={dp.jurisdiction} showLabel />
                <span>{dp.label}</span>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      <div className="flex items-center justify-between px-6 py-3 gap-4">
        <button onClick={() => setExpanded(!expanded)} className="group flex items-center gap-3 rounded-lg px-3 py-2 -mx-1 hover:bg-primary/5 transition-colors">
          <div className="relative">
            <div className={`inline-flex items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground h-10 min-w-10 px-2.5 text-base font-bold shadow-md transition-transform ${pulse ? "scale-125" : "scale-100"}`}>
              {count}
            </div>
            {pulse && <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping pointer-events-none" />}
            {delta !== null && delta > 0 && (
              <span className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 shadow animate-fade-in">
                <Sparkles className="h-2.5 w-2.5" />+{delta}
              </span>
            )}
          </div>
          <div className="flex flex-col items-start text-left">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Database className="h-3.5 w-3.5 text-primary" />
              data points in your dataset
            </span>
            <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors flex items-center gap-1">
              {expanded ? (<>Hide list <ChevronDown className="h-3 w-3" /></>) : (<>Click to review what's included <ChevronUp className="h-3 w-3" /></>)}
            </span>
          </div>
        </button>

        <div className="flex items-center gap-3">
          {showBack && onBack && <Button variant="outline" onClick={onBack}>Back</Button>}
          <Button onClick={onNext}>{nextLabel}</Button>
        </div>
      </div>
    </div>
  );
}
